import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ArtifactStatus, IArtifactDTO } from '../../../../../../api/artifacts.dao';
import DefaultButton from '../../../../../global/default-button/default-button';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { Popover } from '../../../../../global/popover/popover';
import { CopyButton } from '../../../../../global/copy-button/copy-button';
import './artifact-link-wrapper.scss';
import { DELETE_TEXT } from '../../../../../../constants/common-labels.constants';

export type ArtifactLinkWrapperProps = {
    artifact: IArtifactDTO;
    createLink: (id: number, cb: () => void) => void;
    deleteArtifactDownloadLinkPermission: boolean;
    onDeleteArtifactDownloadLink: (assetMetadataId: number) => void;
};

export const ArtifactLinkWrapper = observer((props: ArtifactLinkWrapperProps): JSX.Element | null => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLinkCreating, setIsLinkCreating] = useState<boolean>(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const {
        artifact,
        createLink,
        deleteArtifactDownloadLinkPermission,
        onDeleteArtifactDownloadLink
    } = props;

    const onLinkCreated = (publicLink?: string): void => {
        if (publicLink) setIsOpen(true);
        setIsLinkCreating(false);
    };

    if (artifact.assetStatus !== ArtifactStatus.READY) return null;

    return (
        <div
            className='artifact-create-link-wrapper'
            ref={wrapperRef}
        >
            {isLinkCreating
                ? <span className='artifact-link-creating'>
                    <FormattedMessage
                        id='oe.artifact.link.linkCreating'
                        description='Label'
                        defaultMessage='Creating Link'
                    />
                </span>
                : artifact.publicDownloadLink
                    ? <DefaultButton
                        text={<FormattedMessage
                            id='oe.artifact.link.linkCreated'
                            description='Button text'
                            defaultMessage='Link Created'
                        />}
                        className={classnames('artifact-link artifact-link-created', {
                            'active': isOpen
                        })}
                        modifier='row'
                        onClick={() => setIsOpen(prev => !prev)}
                    />
                    : hasPermission(Permission.CREATE_ARTIFACT_PUBLIC_DOWNLOAD_LINK)
                        ? <DefaultButton
                            text={<FormattedMessage
                                id='oe.artifact.link.createLink'
                                description='Button text'
                                defaultMessage='{prefix} Create Link'
                                values={{ prefix: '+' }}
                            />}
                            className='artifact-link visible-while-hovered'
                            modifier='plus'
                            onClick={() => {
                                setIsLinkCreating(true);
                                createLink(artifact.assetMetadataId, onLinkCreated);
                            }}
                        />
                        : null
            }
            <Popover
                isOpen={isOpen}
                buttonRef={wrapperRef}
                withCloseIcon
                onClose={() => setIsOpen(false)}
                onClickOutside={() => setIsOpen(false)}
                customWidth={210}
                padding={0}
                align='start'
                position='top'
            >
                <div className='artifact-create-link-info'>
                    <div className='artifact-create-link-info__url'>
                        URL:
                    </div>
                    <div className='artifact-create-link-info__link'>
                        {artifact.publicDownloadLink}
                    </div>
                    <div>
                        <CopyButton
                            copyValue={artifact.publicDownloadLink}
                            text={'Copy'}
                            withoutIcon
                            withCopyLink
                        />
                    </div>
                </div>
                {deleteArtifactDownloadLinkPermission &&
                    <div
                        className='popup-footer-remove-button artifact-create-link-footer'
                        onClick={() => onDeleteArtifactDownloadLink(artifact.assetMetadataId)}
                    >
                        <FormattedMessage {...DELETE_TEXT} />
                    </div>
                }
            </Popover>
        </div>
    );
});
